import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import Header from '../../../components/layout/Header';
import AdvancedSearch from '../../../components/leads/AdvancedSearch';
import LeadTable from '../../../components/leads/LeadTable';
import { Button, Modal } from '../../../components/common/Common';
import { apiFetch } from '../../../utils/api';
import useLeads from '../../../hooks/useLeads';
import { useAuth } from '../../../context/AuthContext';
import { useCRM } from '../../../context/CRMContext';
import { Plus, Upload, FileSpreadsheet, Download, CheckCircle, AlertCircle, AlertTriangle, FileText, ChevronLeft, ChevronRight, Eye, UserPlus, X } from 'lucide-react';

const EXPECTED_COLUMNS = [
  // Contact
  'name', 'country_code', 'mobile', 'email',
  'alt_country_code', 'alt_mobile', 'alt_email',
  // Source / broker / referral
  'source', 'broker',
  'ref_name', 'ref_country_code', 'ref_mobile', 'ref_email',
  // Classification
  'buyer_type', 'investment_type', 'status', 'priority',
  // Location
  'country', 'state', 'city', 'locality',
  // Requirements
  'service_type', 'project', 'property_type', 'configuration',
  'min_area', 'max_area', 'area_unit',
  'min_budget', 'max_budget', 'budget_currency',
  'handover_preference', 'post_handover_plan',
  'other_details',
];

// Accept common header variations and map them to the canonical column the backend expects.
// Keys are normalized (lowercased, spaces/punctuation → underscores).
const COLUMN_ALIASES = {
  // name
  full_name: 'name',
  fullname: 'name',
  customer_name: 'name',
  contact_name: 'name',
  lead_name: 'name',
  client_name: 'name',
  first_name: 'name',

  // mobile
  phone: 'mobile',
  phone_number: 'mobile',
  phone_no: 'mobile',
  phoneno: 'mobile',
  contact: 'mobile',
  contact_no: 'mobile',
  contact_number: 'mobile',
  mobile_number: 'mobile',
  mobile_no: 'mobile',
  whatsapp: 'mobile',
  whatsapp_number: 'mobile',
  number: 'mobile',
  cell: 'mobile',
  cell_number: 'mobile',

  // email
  email_address: 'email',
  email_id: 'email',
  emailid: 'email',
  e_mail: 'email',

  // country code
  dial_code: 'country_code',
  code: 'country_code',
  phone_code: 'country_code',
  countrycode: 'country_code',

  // alt contact
  alternate_mobile: 'alt_mobile',
  alternate_phone: 'alt_mobile',
  secondary_mobile: 'alt_mobile',
  alternate_email: 'alt_email',
  secondary_email: 'alt_email',
  alternate_country_code: 'alt_country_code',

  // source / broker / referral
  lead_source: 'source',
  source_name: 'source',
  broker_name: 'broker',
  brokerid: 'broker',
  broker_id: 'broker',
  referral_name: 'ref_name',
  referrer_name: 'ref_name',
  referral_mobile: 'ref_mobile',
  referrer_mobile: 'ref_mobile',
  referral_email: 'ref_email',
  referrer_email: 'ref_email',
  referral_country_code: 'ref_country_code',

  // classification
  type: 'buyer_type',
  buyertype: 'buyer_type',
  investmenttype: 'investment_type',
  status_name: 'status',
  lead_status: 'status',
  ls_name: 'status',
  priority_name: 'priority',
  lead_priority: 'priority',
  lp_name: 'priority',

  // requirements
  service: 'service_type',
  service_types: 'service_type',
  servicetype: 'service_type',
  st_name: 'service_type',
  project_name: 'project',
  projects: 'project',
  propertytype: 'property_type',
  property_types: 'property_type',
  pt_name: 'property_type',
  config: 'configuration',
  configurations: 'configuration',
  configuration_name: 'configuration',
  pc_name: 'configuration',
  bhk: 'configuration',
  minarea: 'min_area',
  maxarea: 'max_area',
  area_min: 'min_area',
  area_max: 'max_area',
  unit: 'area_unit',
  areaunit: 'area_unit',
  minbudget: 'min_budget',
  maxbudget: 'max_budget',
  budget_min: 'min_budget',
  budget_max: 'max_budget',
  price_min: 'min_budget',
  price_max: 'max_budget',
  currency: 'budget_currency',
  budgetcurrency: 'budget_currency',
  handover: 'handover_preference',
  handover_pref: 'handover_preference',
  post_handover: 'post_handover_plan',
  posthandover: 'post_handover_plan',

  // misc
  notes: 'other_details',
  note: 'other_details',
  remarks: 'other_details',
  remark: 'other_details',
  comments: 'other_details',
  comment: 'other_details',
  details: 'other_details',
};

const REQUIRED_COLUMNS = ['name', 'mobile'];

// Mirror of the backend mobile validation so bad rows surface in the preview,
// before the user even hits Import. Rejects multiple-numbers-in-one-cell and
// anything that isn't a clean 10-digit number (leading 91 / 0 tolerated).
const checkMobile = (raw) => {
  const s = String(raw ?? '').trim();
  if (!s) return { ok: false, reason: 'Missing mobile' };
  if (/[/,;|\n]/.test(s)) return { ok: false, reason: 'Multiple numbers in one field' };
  let d = s.replace(/[^0-9]/g, '');
  if (!d) return { ok: false, reason: 'Mobile has no digits' };
  if (d.length === 12 && d.startsWith('91')) d = d.slice(2);
  else if (d.length === 11 && d.startsWith('0')) d = d.slice(1);
  if (d.length !== 10) return { ok: false, reason: `Invalid mobile (${d.length} digits, need 10)` };
  return { ok: true, mobile: d };
};

// Optional fields validated in the preview (non-blocking — row still imports).
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMAIL_FIELDS = ['email', 'alt_email', 'ref_email'];
const PHONE_FIELDS = ['alt_mobile', 'ref_mobile'];
const NUMERIC_FIELDS = ['min_budget', 'max_budget', 'min_area', 'max_area', 'post_handover_plan'];

const normalizeHeader = (raw) =>
  String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/[^\w\s]/g, '')   // strip punctuation like - . / ( )
    .replace(/\s+/g, '_');

const canonicalHeader = (raw) => {
  const norm = normalizeHeader(raw);
  return COLUMN_ALIASES[norm] || norm;
};

const ImportLeads = () => {
  const navigate = useNavigate();
  const { hasPermission, user } = useAuth();
  const { fetchUsers, refreshCounts } = useCRM();
  const { leads, loading, pagination, filters, setFilters, onSearch, onPageChange, refresh } = useLeads({ is_imported: true });
  const [showImport, setShowImport] = useState(false);
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState(null); // { headers, rows }
  const [previewPage, setPreviewPage] = useState(0);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [step, setStep] = useState(1); // 1=upload, 2=preview, 3=result
  const [assignUsers, setAssignUsers] = useState([]); // [{u_id, username, role_slug}, ...]
  const [assignedUserIds, setAssignedUserIds] = useState([]);
  const [showAssignPicker, setShowAssignPicker] = useState(false);
  const [assignSearch, setAssignSearch] = useState('');
  const [editingCell, setEditingCell] = useState(null); // { rowIdx, colIdx }
  const [editingValue, setEditingValue] = useState('');
  const fileRef = useRef(null);
  const editInputRef = useRef(null);
  const PREVIEW_PER_PAGE = 10;

  const role = user?.role_slug || user?.roleSlug;
  const canAssignOnImport = role === 'master' || role === 'admin';

  useEffect(() => {
    if (!showImport || !canAssignOnImport || assignUsers.length > 0) return;
    (async () => {
      const res = await fetchUsers();
      const list = res?.success ? (Array.isArray(res.data) ? res.data : res.data?.users || []) : [];
      setAssignUsers(list);
    })();
  }, [showImport, canAssignOnImport, fetchUsers, assignUsers.length]);

  useEffect(() => {
    if (editingCell && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingCell]);

  const parseFile = useCallback((f) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });

        if (json.length < 2) {
          setResult({ success: false, message: 'File is empty or has no data rows' });
          return;
        }

        const headers = json[0].map(canonicalHeader);
        const rows = json.slice(1).filter(row => row.some(cell => cell !== ''));

        setPreviewData({ headers, rows });
        setSelectedRows(new Set(rows.map((_, i) => i)));
        setPreviewPage(0);
        setStep(2);
        setResult(null);
      } catch (err) {
        setResult({ success: false, message: 'Failed to parse file. Make sure it\'s a valid CSV/Excel file.' });
      }
    };
    reader.readAsArrayBuffer(f);
  }, []);

  const handleFile = (f) => {
    if (!f) return;
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['csv', 'xlsx', 'xls'].includes(ext)) {
      setResult({ success: false, message: 'Only CSV, XLSX, XLS files are supported' });
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      setResult({ success: false, message: 'File size must be under 5MB' });
      return;
    }
    setFile(f);
    setResult(null);
    parseFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const toggleRow = (idx) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const toggleAll = () => {
    if (!previewData) return;
    if (selectedRows.size === previewData.rows.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(previewData.rows.map((_, i) => i)));
    }
  };

  const handleUpload = async ({ forceDuplicates = false, rowsOverride = null } = {}) => {
    if (!file || !previewData) return;
    setUploading(true);
    setResult(null);
    try {
      // Build selected rows back to array and send as JSON
      const selectedData = rowsOverride || previewData.rows
        .filter((_, i) => selectedRows.has(i))
        .map(row => {
          const obj = {};
          previewData.headers.forEach((h, i) => { obj[h] = row[i] || ''; });
          return obj;
        });

      const res = await apiFetch('leads/import', {
        method: 'POST',
        body: JSON.stringify({
          leads: selectedData,
          assign_to_user_ids: canAssignOnImport ? assignedUserIds : undefined,
          allow_duplicates: forceDuplicates,
        }),
      });

      if (res.success) {
        const d = res.data || {};
        const imported = Number(d.imported || 0);
        const duplicates = Number(d.duplicates || 0);
        const errors = Number(d.errors || 0);
        const total = Number(d.total || selectedData.length);

        if (imported === 0) {
          // API returned OK but nothing actually got saved — treat as failure so user knows.
          const parts = [];
          if (duplicates > 0) parts.push(`${duplicates} duplicate${duplicates !== 1 ? 's' : ''} skipped`);
          if (errors > 0) parts.push(`${errors} row${errors !== 1 ? 's' : ''} had errors (missing name/mobile?)`);
          setResult({
            success: false,
            message: `No leads were imported out of ${total}.${parts.length ? ' ' + parts.join(', ') + '.' : ''}`,
            details: d,
          });
          setStep(3);
        } else {
          const parts = [`${imported} lead${imported !== 1 ? 's' : ''} imported`];
          if (duplicates > 0) parts.push(`${duplicates} duplicate${duplicates !== 1 ? 's' : ''} skipped`);
          if (errors > 0) parts.push(`${errors} error${errors !== 1 ? 's' : ''}`);
          setResult({ success: true, message: parts.join(' · '), details: d });
          setStep(3);
          refresh();
          refreshCounts(); // update sidebar Leads/Import counts without a page refresh
        }
      } else {
        setResult({ success: false, message: res.message || 'Import failed', details: res.data });
      }
    } catch (err) {
      setResult({ success: false, message: 'Failed to import leads' });
    }
    setUploading(false);
  };

  // Re-import only the rows that were skipped as "already exists in system",
  // with explicit permission. Sends just those rows so the already-imported
  // (non-duplicate) rows are not inserted a second time.
  const reimportDuplicates = () => {
    const dupSet = new Set(result?.details?.dbDuplicateMobiles || []);
    if (dupSet.size === 0) return;
    const rows = previewData.rows
      .filter((_, i) => selectedRows.has(i))
      .map(row => {
        const obj = {};
        previewData.headers.forEach((h, i) => { obj[h] = row[i] || ''; });
        return obj;
      })
      .filter(obj => {
        const c = checkMobile(obj.mobile);
        return c.ok && dupSet.has(c.mobile);
      });
    if (rows.length === 0) return;
    handleUpload({ forceDuplicates: true, rowsOverride: rows });
  };

  const closeModal = () => {
    setShowImport(false);
    setFile(null);
    setPreviewData(null);
    setSelectedRows(new Set());
    setResult(null);
    setStep(1);
    setAssignedUserIds([]);
    setShowAssignPicker(false);
    setAssignSearch('');
    setEditingCell(null);
    refresh();
  };

  const goBack = () => {
    setStep(1);
    setFile(null);
    setPreviewData(null);
    setSelectedRows(new Set());
    setResult(null);
    setEditingCell(null);
  };

  const startEditCell = (rowIdx, colIdx) => {
    if (!previewData) return;
    setEditingValue(previewData.rows[rowIdx][colIdx] || '');
    setEditingCell({ rowIdx, colIdx });
  };

  const commitEdit = () => {
    if (!editingCell || !previewData) { setEditingCell(null); return; }
    const { rowIdx, colIdx } = editingCell;
    const next = previewData.rows.map((r, i) => {
      if (i !== rowIdx) return r;
      const nr = [...r];
      nr[colIdx] = editingValue;
      return nr;
    });
    setPreviewData({ ...previewData, rows: next });
    setEditingCell(null);
  };

  const cancelEdit = () => setEditingCell(null);

  const toggleAssignUser = (uid) => {
    setAssignedUserIds(prev => prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]);
  };

  const filteredAssignUsers = assignSearch.trim()
    ? assignUsers.filter(u => (u.username || '').toLowerCase().includes(assignSearch.trim().toLowerCase()))
    : assignUsers;

  // Preview pagination
  const totalPreviewPages = previewData ? Math.ceil(previewData.rows.length / PREVIEW_PER_PAGE) : 0;
  const pagedRows = previewData ? previewData.rows.slice(previewPage * PREVIEW_PER_PAGE, (previewPage + 1) * PREVIEW_PER_PAGE) : [];
  const pagedStartIdx = previewPage * PREVIEW_PER_PAGE;

  const getColumnStatus = (header) => {
    if (EXPECTED_COLUMNS.includes(header)) return 'matched';
    return 'extra';
  };

  const missingRequired = previewData
    ? REQUIRED_COLUMNS.filter(req => !previewData.headers.includes(req))
    : [];

  // Per-row client-side validation shown in the preview ("there and then").
  // Map of rowIndex -> { blocking, warnings[] }.
  //  - blocking: row WILL be skipped on import (missing name / bad mobile / in-file dup)
  //  - warnings: row still imports, but the listed field value is invalid and the
  //    backend will ignore/blank it (bad email, non-numeric budget/area, bad alt/ref phone).
  // DB duplicates can only be known after import.
  const rowIssues = useMemo(() => {
    if (!previewData) return {};
    const idx = {};
    previewData.headers.forEach((h, i) => { if (!(h in idx)) idx[h] = i; });
    const get = (row, field) => (idx[field] != null ? String(row[idx[field]] ?? '').trim() : '');
    const isNum = (v) => /^-?\d+(\.\d+)?$/.test(v);
    const seen = new Map();
    const issues = {};
    previewData.rows.forEach((row, i) => {
      let blocking = null;
      const warnings = [];

      // Required fields (skip the row)
      const name = get(row, 'name');
      if (!name) blocking = 'Missing name';
      else {
        const chk = checkMobile(get(row, 'mobile'));
        if (!chk.ok) blocking = `Mobile: ${chk.reason}`;
        else if (seen.has(chk.mobile)) blocking = `Duplicate of row ${seen.get(chk.mobile) + 1} in this file`;
        else seen.set(chk.mobile, i);
      }

      // Optional fields (warn, still imports)
      EMAIL_FIELDS.forEach(f => { const v = get(row, f); if (v && !EMAIL_RE.test(v)) warnings.push(`${f}: invalid email`); });
      PHONE_FIELDS.forEach(f => { const v = get(row, f); if (v) { const c = checkMobile(v); if (!c.ok) warnings.push(`${f}: ${c.reason}`); } });
      NUMERIC_FIELDS.forEach(f => { const v = get(row, f); if (v && !isNum(v)) warnings.push(`${f}: not a number (will be ignored)`); });
      const minB = get(row, 'min_budget'), maxB = get(row, 'max_budget');
      if (isNum(minB) && isNum(maxB) && Number(minB) > Number(maxB)) warnings.push('min_budget > max_budget');
      const minA = get(row, 'min_area'), maxA = get(row, 'max_area');
      if (isNum(minA) && isNum(maxA) && Number(minA) > Number(maxA)) warnings.push('min_area > max_area');

      if (blocking || warnings.length) issues[i] = { blocking, warnings };
    });
    return issues;
  }, [previewData]);

  const selectedBlockCount = Object.entries(rowIssues).filter(([i, v]) => v.blocking && selectedRows.has(Number(i))).length;
  const selectedWarnCount = Object.entries(rowIssues).filter(([i, v]) => !v.blocking && v.warnings.length && selectedRows.has(Number(i))).length;

  const canUpload = previewData && missingRequired.length === 0 && selectedRows.size > 0 && !uploading;

  return (
    <div>
      <Header title="Imported Leads" subtitle={`${pagination.total || 0} imported leads`} actions={
        <div style={{ display: 'flex', gap: 8 }}>
          {hasPermission('leads.import') && <Button variant="outline" icon={Upload} onClick={() => setShowImport(true)}>Import</Button>}
          {hasPermission('leads.add') && <Button variant="gold" icon={Plus} onClick={() => navigate('/leads/add')}>Add Lead</Button>}
        </div>
      } />
      <div className="page">
       <AdvancedSearch filters={filters} setFilters={setFilters} onSearch={onSearch} showLeadType />
        <LeadTable
          leads={leads}
          loading={loading}
          pagination={pagination}
          onPageChange={onPageChange}
          onRefresh={refresh}
          showLeadType={true}
          pageAccentColor="#1d4ed8"
          emptyMessage="No imported leads found"
        />
      </div>

      {/* Import Modal */}
      <Modal isOpen={showImport} onClose={closeModal} title={step === 1 ? 'Import Leads' : step === 2 ? 'Preview & Confirm' : 'Import Complete'} size={step === 2 ? 'lg' : 'md'}>
        <div className="imp">

          {/* Step 1: Upload */}
          {step === 1 && (
            <>
              <div className="imp__steps">
                <div className="imp__step imp__step--active"><span>1</span> Upload File</div>
                <div className="imp__step"><span>2</span> Preview</div>
                <div className="imp__step"><span>3</span> Done</div>
              </div>

              <div className="imp__template">
                <FileSpreadsheet size={18} color="#22c55e" />
                <div className="imp__template-info">
                  <p className="imp__template-title">Download Template</p>
                  <p className="imp__template-sub">Use this format for best results</p>
                </div>
                <button className="imp__template-btn" onClick={() => {
                  const sample = {
                    name: 'John Doe',
                    country_code: '+91',
                    mobile: '9876543210',
                    email: 'john@example.com',
                    alt_country_code: '+91',
                    alt_mobile: '',
                    alt_email: '',
                    source: 'Facebook',
                    broker: '',
                    ref_name: '',
                    ref_country_code: '',
                    ref_mobile: '',
                    ref_email: '',
                    buyer_type: 'End User',
                    investment_type: 'Ready to Move',
                    status: 'Fresh Lead',
                    priority: 'High',
                    country: 'India',
                    state: 'Maharashtra',
                    city: 'Mumbai',
                    locality: 'Andheri',
                    service_type: 'Buy',
                    project: '',
                    property_type: 'Apartment',
                    configuration: '2 BHK',
                    min_area: '800',
                    max_area: '1200',
                    area_unit: 'sqft',
                    min_budget: '5000000',
                    max_budget: '8000000',
                    budget_currency: 'INR',
                    handover_preference: 'Ready to Move',
                    post_handover_plan: '0',
                    other_details: 'Sample lead',
                  };
                  // CSV-escape values that contain commas/quotes
                  const esc = (v) => {
                    const s = String(v ?? '');
                    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
                  };
                  const csv =
                    EXPECTED_COLUMNS.join(',') + '\n' +
                    EXPECTED_COLUMNS.map(c => esc(sample[c] ?? '')).join(',');
                  const blob = new Blob([csv], { type: 'text/csv' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a'); a.href = url; a.download = 'lead_import_template.csv'; a.click();
                  URL.revokeObjectURL(url);
                }}>
                  <Download size={14} /> Template
                </button>
              </div>

              <div
                className={`imp__drop ${dragOver ? 'imp__drop--drag' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileRef.current?.click()}
              >
                <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={(e) => handleFile(e.target.files[0])} hidden />
                <Upload size={32} color="#9ca3af" />
                <p className="imp__drop-title">Drag & drop your file here</p>
                <p className="imp__drop-sub">CSV, XLSX, XLS — max 5MB</p>
              </div>

              {result && !result.success && (
                <div className="imp__alert imp__alert--error">
                  <AlertCircle size={16} /> {result.message}
                </div>
              )}
            </>
          )}

          {/* Step 2: Preview */}
          {step === 2 && previewData && (
            <>
              <div className="imp__steps">
                <div className="imp__step imp__step--done"><span>✓</span> Upload</div>
                <div className="imp__step imp__step--active"><span>2</span> Preview</div>
                <div className="imp__step"><span>3</span> Done</div>
              </div>

              {/* File info bar */}
              <div className="imp__file-bar">
                <div className="imp__file-bar-left">
                  <FileText size={18} color="#4285F4" />
                  <div>
                    <p className="imp__file-name">{file?.name}</p>
                    <p className="imp__file-meta">{previewData.rows.length} rows · {previewData.headers.length} columns · {selectedRows.size} selected</p>
                  </div>
                </div>
                <button className="imp__file-change" onClick={goBack}>Change file</button>
              </div>

              {/* Column mapping info */}
              <div className="imp__col-tags">
                {previewData.headers.map((h, i) => (
                  <span key={i} className={`imp__col-tag imp__col-tag--${getColumnStatus(h)}`}>{h}</span>
                ))}
              </div>

              {missingRequired.length > 0 && (
                <div className="imp__alert imp__alert--error">
                  <AlertCircle size={16} />
                  <span>
                    Missing required column{missingRequired.length > 1 ? 's' : ''}: <strong>{missingRequired.join(', ')}</strong>.
                    Rename your CSV header(s) — the template uses <code>name</code> and <code>mobile</code>. (Aliases like <code>Full Name</code>, <code>Phone</code>, <code>Contact Number</code> are also accepted.)
                  </span>
                </div>
              )}

              {selectedBlockCount > 0 && (
                <div className="imp__alert imp__alert--error">
                  <AlertTriangle size={16} />
                  <span>
                    <strong>{selectedBlockCount}</strong> selected row{selectedBlockCount !== 1 ? 's' : ''} will be skipped (missing name, or invalid/multiple/duplicate mobile). Hover the red icon on a row for the reason — fix the cell or uncheck the row.
                  </span>
                </div>
              )}
              {selectedWarnCount > 0 && (
                <div className="imp__alert imp__alert--warn">
                  <AlertTriangle size={16} />
                  <span>
                    <strong>{selectedWarnCount}</strong> selected row{selectedWarnCount !== 1 ? 's' : ''} ha{selectedWarnCount !== 1 ? 've' : 's'} field issues (invalid email/phone, non-numeric budget/area). These rows still import, but the bad value is ignored. Hover the amber icon for details.
                  </span>
                </div>
              )}

              {/* Assign to users (master/admin only) */}
              {canAssignOnImport && (
                <div className="imp__assign">
                  <div className="imp__assign-head">
                    <UserPlus size={14} />
                    <span className="imp__assign-title">Assign these leads to:</span>
                    {assignedUserIds.length === 0 && <span className="imp__assign-muted">No one — leads will be unassigned</span>}
                  </div>
                  <div className="imp__assign-row">
                    {assignedUserIds.map(uid => {
                      const u = assignUsers.find(x => x.u_id === uid);
                      if (!u) return null;
                      return (
                        <span key={uid} className="imp__assign-chip">
                          {u.username}
                          <button type="button" onClick={() => toggleAssignUser(uid)} aria-label="Remove"><X size={11} /></button>
                        </span>
                      );
                    })}
                    <button type="button" className="imp__assign-add" onClick={() => setShowAssignPicker(v => !v)}>
                      <UserPlus size={12} /> {showAssignPicker ? 'Close' : 'Add user'}
                    </button>
                  </div>
                  {showAssignPicker && (
                    <div className="imp__assign-picker">
                      <input
                        type="text"
                        placeholder="Search users..."
                        value={assignSearch}
                        onChange={(e) => setAssignSearch(e.target.value)}
                        className="imp__assign-search"
                      />
                      <div className="imp__assign-list">
                        {filteredAssignUsers.length === 0 ? (
                          <div className="imp__assign-empty">No users found</div>
                        ) : filteredAssignUsers.map(u => (
                          <label key={u.u_id} className="imp__assign-item">
                            <input
                              type="checkbox"
                              checked={assignedUserIds.includes(u.u_id)}
                              onChange={() => toggleAssignUser(u.u_id)}
                            />
                            <span>{u.username}</span>
                            {u.role_name && <span className="imp__assign-role">{u.role_name}</span>}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <p className="imp__edit-hint">💡 <strong>Tip:</strong> Double-click any cell to edit its value before importing.</p>

              {/* Preview Table */}
              <div className="imp__table-wrap">
                <table className="imp__table">
                  <thead>
                    <tr>
                      <th style={{ width: 36 }}>
                        <input type="checkbox" checked={selectedRows.size === previewData.rows.length} onChange={toggleAll} />
                      </th>
                      <th style={{ width: 36 }}>#</th>
                      {previewData.headers.map((h, i) => (
                        <th key={i} className={`imp__th--${getColumnStatus(h)}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pagedRows.map((row, ri) => {
                      const realIdx = pagedStartIdx + ri;
                      const isSelected = selectedRows.has(realIdx);
                      const issue = rowIssues[realIdx];
                      const sev = issue ? (issue.blocking ? 'block' : 'warn') : null;
                      const issueText = issue ? [issue.blocking, ...issue.warnings].filter(Boolean).join(' · ') : '';
                      return (
                        <tr key={realIdx} className={`${isSelected ? '' : 'imp__row--disabled'}${sev && isSelected ? ` imp__row--${sev}` : ''}`}>
                          <td><input type="checkbox" checked={isSelected} onChange={() => toggleRow(realIdx)} /></td>
                          <td className="imp__td-num">
                            {realIdx + 1}
                            {issue && (
                              <span className={`imp__row-issue imp__row-issue--${sev}`} title={issueText}>
                                <AlertTriangle size={13} />
                              </span>
                            )}
                          </td>
                          {previewData.headers.map((_, ci) => {
                            const isEditing = editingCell && editingCell.rowIdx === realIdx && editingCell.colIdx === ci;
                            return (
                              <td
                                key={ci}
                                className={`imp__td-cell imp__td-cell--editable${isEditing ? ' imp__td-cell--editing' : ''}`}
                                onDoubleClick={() => startEditCell(realIdx, ci)}
                                title="Double-click to edit"
                              >
                                {isEditing ? (
                                  <input
                                    ref={editInputRef}
                                    className="imp__cell-input"
                                    value={editingValue}
                                    onChange={(e) => setEditingValue(e.target.value)}
                                    onBlur={commitEdit}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') { e.preventDefault(); commitEdit(); }
                                      else if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); }
                                    }}
                                  />
                                ) : (
                                  row[ci] || <span className="imp__empty">—</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Preview Pagination */}
              {totalPreviewPages > 1 && (
                <div className="imp__paging">
                  <span>Page {previewPage + 1} of {totalPreviewPages}</span>
                  <div className="imp__paging-btns">
                    <button disabled={previewPage <= 0} onClick={() => setPreviewPage(p => p - 1)}><ChevronLeft size={16} /></button>
                    <button disabled={previewPage >= totalPreviewPages - 1} onClick={() => setPreviewPage(p => p + 1)}><ChevronRight size={16} /></button>
                  </div>
                </div>
              )}

              {result && !result.success && (
                <div className="imp__alert imp__alert--error">
                  <AlertCircle size={16} /> {result.message}
                </div>
              )}

              <div className="modal__actions">
                <Button variant="outline" onClick={goBack}>Back</Button>
                <Button variant="gold" icon={Upload} onClick={() => handleUpload()} disabled={!canUpload}>
                  {uploading
                    ? 'Importing...'
                    : `Import ${selectedRows.size} Lead${selectedRows.size !== 1 ? 's' : ''}${canAssignOnImport && assignedUserIds.length > 0 ? ` → ${assignedUserIds.length} user${assignedUserIds.length !== 1 ? 's' : ''}` : ''}`}
                </Button>
              </div>
            </>
          )}

          {/* Step 3: Result */}
          {step === 3 && (
            <>
              <div className="imp__steps">
                <div className="imp__step imp__step--done"><span>✓</span> Upload</div>
                <div className="imp__step imp__step--done"><span>✓</span> Preview</div>
                <div className="imp__step imp__step--active"><span>3</span> Done</div>
              </div>

              <div className="imp__result-card">
                {result?.success ? <CheckCircle size={40} color="#22c55e" /> : <AlertCircle size={40} color="#ef4444" />}
                <h3 className="imp__result-title">{result?.success ? 'Import Successful!' : 'Import Failed'}</h3>
                <p className="imp__result-msg">{result?.message}</p>
                {result?.details?.errors > 0 && <p className="imp__result-sub">{result.details.errors} rows had errors</p>}
                {result?.details?.duplicates > 0 && <p className="imp__result-sub">{result.details.duplicates} duplicates skipped</p>}
              </div>

              {Array.isArray(result?.details?.skipped) && result.details.skipped.length > 0 && (
                <div className="imp__skip">
                  <p className="imp__skip-head">Skipped rows ({result.details.skipped.length}):</p>
                  <div className="imp__skip-wrap">
                    <table className="imp__skip-table">
                      <thead>
                        <tr><th>Row</th><th>Name</th><th>Mobile</th><th>Reason</th></tr>
                      </thead>
                      <tbody>
                        {result.details.skipped.map((s, i) => (
                          <tr key={i}>
                            <td>{s.row}</td>
                            <td>{s.name || '—'}</td>
                            <td>{s.mobile || '—'}</td>
                            <td className="imp__skip-reason">{s.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {((result.details.duplicates || 0) + (result.details.errors || 0)) > result.details.skipped.length && (
                    <p className="imp__skip-more">…and {((result.details.duplicates || 0) + (result.details.errors || 0)) - result.details.skipped.length} more not shown</p>
                  )}
                </div>
              )}

              {Array.isArray(result?.details?.dbDuplicateMobiles) && result.details.dbDuplicateMobiles.length > 0 && (
                <div className="imp__dup-note">
                  <p>{result.details.dbDuplicateMobiles.length} lead{result.details.dbDuplicateMobiles.length !== 1 ? 's' : ''} already exist in the system. You can import {result.details.dbDuplicateMobiles.length !== 1 ? 'them' : 'it'} again if needed.</p>
                  <Button variant="outline" icon={Upload} onClick={reimportDuplicates} disabled={uploading}>
                    {uploading ? 'Importing...' : `Import existing lead${result.details.dbDuplicateMobiles.length !== 1 ? 's' : ''} anyway`}
                  </Button>
                </div>
              )}

              <div className="modal__actions">
                <Button variant="outline" onClick={closeModal}>Close</Button>
                {result?.success && <Button variant="gold" icon={Eye} onClick={() => { closeModal(); refresh(); }}>View Imported</Button>}
              </div>
            </>
          )}
        </div>
      </Modal>

      <style>{`
        .imp { display: flex; flex-direction: column; gap: 18px; }

        /* Steps */
        .imp__steps { display: flex; gap: 4px; }
        .imp__step { display: flex; align-items: center; gap: 6px; font-size: 12px; color: var(--gray-400); flex: 1; padding: 10px 12px; background: var(--gray-50); border-radius: 8px; font-weight: 500; }
        .imp__step span { width: 22px; height: 22px; border-radius: 50%; background: var(--gray-200); color: var(--gray-500); font-size: 11px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .imp__step--active { background: var(--navy-50); color: var(--navy-700); }
        .imp__step--active span { background: var(--navy-700); color: white; }
        .imp__step--done { background: #f0fdf4; color: #16a34a; }
        .imp__step--done span { background: #22c55e; color: white; }

        /* Template */
        .imp__template { display: flex; align-items: center; gap: 12px; padding: 12px 16px; background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 10px; }
        .imp__template-info { flex: 1; }
        .imp__template-title { font-size: 13px; font-weight: 600; color: var(--gray-800); margin: 0; }
        .imp__template-sub { font-size: 11px; color: var(--gray-500); margin: 2px 0 0; }
        .imp__template-btn { display: flex; align-items: center; gap: 5px; padding: 6px 12px; border: 1px solid #86efac; border-radius: 6px; background: white; font-size: 12px; font-weight: 600; color: #16a34a; cursor: pointer; white-space: nowrap; }
        .imp__template-btn:hover { background: #f0fdf4; }

        /* Dropzone */
        .imp__drop { border: 2px dashed var(--gray-300); border-radius: 12px; padding: 36px; text-align: center; cursor: pointer; transition: all 0.2s; display: flex; flex-direction: column; align-items: center; gap: 6px; }
        .imp__drop:hover { border-color: var(--navy-400); background: var(--gray-50); }
        .imp__drop--drag { border-color: #4285F4; background: #eff6ff; }
        .imp__drop-title { font-size: 14px; font-weight: 600; color: var(--gray-600); margin: 0; }
        .imp__drop-sub { font-size: 12px; color: var(--gray-400); margin: 0; }

        /* File bar */
        .imp__file-bar { display: flex; align-items: center; justify-content: space-between; padding: 10px 14px; background: var(--gray-50); border-radius: 10px; border: 1px solid var(--gray-200); }
        .imp__file-bar-left { display: flex; align-items: center; gap: 10px; }
        .imp__file-name { font-size: 13px; font-weight: 600; color: var(--gray-800); margin: 0; }
        .imp__file-meta { font-size: 11px; color: var(--gray-500); margin: 2px 0 0; }
        .imp__file-change { border: none; background: none; color: var(--navy-600); font-size: 12px; font-weight: 600; cursor: pointer; text-decoration: underline; }

        /* Column tags */
        .imp__col-tags { display: flex; gap: 6px; flex-wrap: wrap; }
        .imp__col-tag { padding: 3px 10px; border-radius: 12px; font-size: 11px; font-weight: 600; }
        .imp__col-tag--matched { background: #dbeafe; color: #1d4ed8; }
        .imp__col-tag--extra { background: var(--gray-100); color: var(--gray-500); }

        /* Preview Table */
        .imp__table-wrap { overflow-x: auto; border: 1px solid var(--gray-200); border-radius: 10px; max-height: 360px; overflow-y: auto; }
        .imp__table { width: 100%; border-collapse: collapse; font-size: 12px; }
        .imp__table thead { position: sticky; top: 0; z-index: 1; }
        .imp__table th { padding: 8px 10px; background: var(--gray-50); border-bottom: 1px solid var(--gray-200); font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.3px; color: var(--gray-500); white-space: nowrap; text-align: left; }
        .imp__th--matched { color: #1d4ed8; }
        .imp__th--extra { color: var(--gray-400); }
        .imp__table td { padding: 7px 10px; border-bottom: 1px solid var(--gray-100); white-space: nowrap; max-width: 180px; overflow: hidden; text-overflow: ellipsis; }
        .imp__td-num { color: var(--gray-400); font-weight: 500; white-space: nowrap; }
        .imp__row-issue { display: inline-flex; vertical-align: middle; margin-left: 5px; cursor: help; }
        .imp__row-issue--block { color: #ef4444; }
        .imp__row-issue--warn { color: #f59e0b; }
        .imp__td-cell { color: var(--gray-700); }
        .imp__empty { color: var(--gray-300); }
        .imp__row--disabled { opacity: 0.35; }
        .imp__row--block td { background: #fef2f2; }
        .imp__row--warn td { background: #fffbeb; }
        .imp__table input[type="checkbox"] { cursor: pointer; width: 15px; height: 15px; accent-color: var(--navy-700); }

        /* Paging */
        .imp__paging { display: flex; align-items: center; justify-content: space-between; font-size: 12px; color: var(--gray-500); }
        .imp__paging-btns { display: flex; gap: 4px; }
        .imp__paging-btns button { width: 28px; height: 28px; border-radius: 6px; border: 1px solid var(--gray-200); background: var(--white); display: flex; align-items: center; justify-content: center; cursor: pointer; color: var(--gray-500); }
        .imp__paging-btns button:disabled { opacity: 0.3; cursor: not-allowed; }

        /* Alert */
        .imp__alert { display: flex; align-items: center; gap: 8px; padding: 10px 14px; border-radius: 8px; font-size: 12px; font-weight: 500; }
        .imp__alert--error { background: #fef2f2; color: #ef4444; }
        .imp__alert--warn { background: #fffbeb; color: #b45309; border: 1px solid #fde68a; }

        /* Result */
        .imp__result-card { text-align: center; padding: 24px; }
        .imp__result-title { font-size: 18px; font-weight: 700; color: var(--gray-800); margin: 12px 0 6px; }
        .imp__result-msg { font-size: 13px; color: var(--gray-600); margin: 0; }
        .imp__result-sub { font-size: 12px; color: var(--gray-500); margin: 4px 0 0; }

        /* Skipped rows detail */
        .imp__skip { margin-top: 4px; }
        .imp__skip-head { font-size: 12px; font-weight: 600; color: var(--gray-700); margin: 0 0 6px; }
        .imp__skip-wrap { border: 1px solid var(--gray-200); border-radius: 8px; max-height: 220px; overflow-y: auto; }
        .imp__skip-table { width: 100%; border-collapse: collapse; font-size: 12px; }
        .imp__skip-table thead { position: sticky; top: 0; z-index: 1; }
        .imp__skip-table th { padding: 7px 10px; background: var(--gray-50); border-bottom: 1px solid var(--gray-200); font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.3px; color: var(--gray-500); text-align: left; }
        .imp__skip-table td { padding: 6px 10px; border-bottom: 1px solid var(--gray-100); color: var(--gray-700); }
        .imp__skip-reason { color: #b45309; font-weight: 500; }
        .imp__skip-more { font-size: 11px; color: var(--gray-400); margin: 6px 0 0; }
        .imp__dup-note { margin-top: 14px; padding: 12px; background: var(--gray-50); border: 1px solid var(--gray-200); border-radius: 8px; display: flex; flex-direction: column; gap: 10px; align-items: flex-start; }
        .imp__dup-note p { font-size: 12px; color: var(--gray-600); margin: 0; }

        /* Assign on import */
        .imp__assign { background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 10px; padding: 10px 12px; }
        .imp__assign-head { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #1e40af; font-weight: 600; margin-bottom: 8px; }
        .imp__assign-title { color: #1e3a8a; }
        .imp__assign-muted { color: var(--gray-500); font-weight: 400; font-style: italic; margin-left: 4px; }
        .imp__assign-row { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
        .imp__assign-chip { display: inline-flex; align-items: center; gap: 5px; padding: 3px 4px 3px 10px; background: #2563eb; color: white; border-radius: 12px; font-size: 11px; font-weight: 600; }
        .imp__assign-chip button { display: flex; align-items: center; justify-content: center; width: 16px; height: 16px; border: none; border-radius: 50%; background: rgba(255,255,255,0.25); color: white; cursor: pointer; padding: 0; }
        .imp__assign-chip button:hover { background: rgba(255,255,255,0.4); }
        .imp__assign-add { display: inline-flex; align-items: center; gap: 4px; padding: 4px 10px; background: white; border: 1px dashed #93c5fd; color: #1d4ed8; border-radius: 12px; font-size: 11px; font-weight: 600; cursor: pointer; }
        .imp__assign-add:hover { background: #eff6ff; border-style: solid; }
        .imp__assign-picker { margin-top: 10px; background: white; border: 1px solid var(--gray-200); border-radius: 8px; padding: 8px; max-height: 220px; display: flex; flex-direction: column; gap: 6px; }
        .imp__assign-search { width: 100%; padding: 6px 10px; border: 1px solid var(--gray-200); border-radius: 6px; font-size: 12px; outline: none; }
        .imp__assign-search:focus { border-color: var(--navy-400); }
        .imp__assign-list { overflow-y: auto; max-height: 160px; display: flex; flex-direction: column; gap: 2px; }
        .imp__assign-item { display: flex; align-items: center; gap: 8px; padding: 5px 8px; border-radius: 5px; font-size: 12px; cursor: pointer; }
        .imp__assign-item:hover { background: var(--gray-50); }
        .imp__assign-item input { cursor: pointer; }
        .imp__assign-role { margin-left: auto; font-size: 10px; color: var(--gray-500); background: var(--gray-100); padding: 1px 6px; border-radius: 3px; text-transform: uppercase; letter-spacing: 0.3px; font-weight: 600; }
        .imp__assign-empty { padding: 10px; text-align: center; color: var(--gray-400); font-size: 12px; }

        /* Editable cells */
        .imp__edit-hint { margin: 0; font-size: 12px; color: var(--gray-500); }
        .imp__td-cell--editable { cursor: text; }
        .imp__td-cell--editable:hover { background: #fffbeb; outline: 1px dashed #fbbf24; outline-offset: -1px; }
        .imp__td-cell--editing { padding: 0 !important; }
        .imp__cell-input { width: 100%; padding: 7px 10px; border: 2px solid var(--navy-500, #4285F4); border-radius: 0; background: white; font-size: 12px; outline: none; color: var(--gray-800); }
      `}</style>
    </div>
  );
};
export default ImportLeads;
